import { Grid, Typography } from "@material-ui/core";
import { ChevronRight } from "@material-ui/icons";
import { App, Events } from "app-interfaces";
import React from "react";
import { useTranslation } from "react-i18next";

import { useStateValue } from "../../../config/AppContext";
import EventsClass from "../../../models/Events";
import { formatDate } from "../../../utils/time-utils";
import { SessionTimePicker, TimePickerModal } from "../../Modals";
import LoadingSpinner from "../../Utilities/LoadingSpinner";

const DefaultSessionLengthsAndBreaks: React.FC = () => {
  const { t } = useTranslation();
  const { events, settings, Methods } = useStateValue() as {
    events: EventsClass;
    settings: App.State["settings"];
    Methods: App.Methods;
  };
  const [selected, setSelected] = React.useState<{
    session_length?: number;
    session_length_long?: number;
    session_interval?: number;
  } | null>(null);
  const [title, setTitle] = React.useState<string | null>(null);
  const [lunch, setLunch] = React.useState<Events.ILunch | null>(null);
  const [currentItem, setCurrentItem] = React.useState<{
    start: string;
    end: string;
    datetime?: Date;
  } | null>(null);

  const handleClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const { id } = e.currentTarget;
    if (!settings) return;
    switch (id) {
      case "SHORT":
        setSelected({ session_length: settings.session_length });
        break;
      case "LONG":
        setSelected({ session_length_long: settings.session_length_long });
        break;
      case "INTERVAL":
        setSelected({ session_interval: settings.session_interval });
        break;
      default:
        break;
    }
  };
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!settings) return;
    const formData = new FormData(e.currentTarget);
    const session_length = formData.get("session_length");
    const session_length_long = formData.get("session_length_long");
    const session_interval = formData.get("session_interval");
    const params = settings;
    if (session_length && typeof session_length === "string") {
      params.session_length = parseInt(session_length);
    }
    if (session_length_long && typeof session_length_long === "string") {
      params.session_length_long = parseInt(session_length_long);
    }
    if (session_interval && typeof session_interval === "string") {
      params.session_interval = parseInt(session_interval);
    }
    setSelected(null);
    await Methods.updateProfile(params);
  };
  const handleLunch = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const start = formData.get("from");
    const end = formData.get("to");
    if (!lunch || !start || typeof start !== "string" || !end || typeof end !== "string") return;
    const updatedItem = {
      ...lunch,
      id: null,
      created: formatDate(new Date(), 6),
      start: start.split(" ")[0] + "Z",
      end: end.split(" ")[0] + "Z",
    };
    setLunch(null);
    await Methods.delEvents(lunch.id);
    await Methods.postEvents(updatedItem);
  };

  React.useEffect(() => {
    if (!selected) {
      setTitle(null);
    } else if (selected.session_length) {
      setTitle(t("Default Short Session Length"));
    } else if (selected.session_length_long) {
      setTitle(t("Default Long Session Length"));
    } else if (selected.session_interval) {
      setTitle(t("Default Pause Between Sessions"));
    }
  }, [selected, t]);
  React.useEffect(() => {
    if (!lunch) {
      setCurrentItem(null);
    } else if (lunch) {
      if (lunch.start && lunch.end) setCurrentItem({ start: lunch.start, end: lunch.end });
    }
  }, [lunch]);
  if (settings) {
    return (
      <>
        <Typography variant="subtitle1" gutterBottom style={{ marginTop: 15 }}>
          {t("Default session lengths")}
        </Typography>
        <Typography variant="caption" component="i" style={{ marginTop: 0 }}>
          {t("These lengths will be shown on the booking site for clients")}
        </Typography>
        <hr />
        <Grid id="SHORT" container spacing={3} onClick={handleClick} style={{ cursor: "pointer" }}>
          <Grid item xs={6}>
            <Typography variant="body1">{t("Session length short")}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" component="span">
              {settings.session_length} {t("Minutes")}
              <ChevronRight />
            </Typography>
          </Grid>
        </Grid>
        <Grid id="LONG" container spacing={3} onClick={handleClick} style={{ cursor: "pointer" }}>
          <Grid item xs={6}>
            <Typography variant="body1">{t("Session length long")}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" component="span">
              {settings.session_length_long} {t("Minutes")}
              <ChevronRight />
            </Typography>
          </Grid>
        </Grid>
        <hr />

        <Typography variant="subtitle1">{t("Default breaks")}</Typography>
        <hr />
        <Grid
          id="INTERVAL"
          container
          spacing={3}
          onClick={handleClick}
          style={{ cursor: "pointer" }}
        >
          <Grid item xs={6}>
            <Typography variant="body1">{t("Pause between sessions")}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" component="span">
              {settings.session_interval} {t("Minutes")}
              <ChevronRight />
            </Typography>
          </Grid>
        </Grid>
        <Grid
          container
          spacing={3}
          onClick={() => setLunch(events.lunch[events.lunch.length - 1])}
          style={{ cursor: "pointer" }}
        >
          <Grid item xs={6}>
            <Typography variant="body1">{t("Default lunch break")}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" component="span">
              {`${
                events &&
                events.lunch &&
                events.lunch.length > 0 &&
                events.lunch[events.lunch.length - 1].start
              }`.replace("Z", "")}{" "}
              -{" "}
              {`${
                events &&
                events.lunch &&
                events.lunch.length > 0 &&
                events.lunch[events.lunch.length - 1].end
              }`.replace("Z", "")}
              <ChevronRight />
            </Typography>
          </Grid>
        </Grid>
        <SessionTimePicker
          show={!!selected}
          onHide={() => setSelected(null)}
          onSubmit={handleSubmit}
          currentItem={selected ? selected : undefined}
          title={title}
        />
        {currentItem && (
          <TimePickerModal
            show={!!lunch}
            onHide={() => setLunch(null)}
            onSubmit={handleLunch}
            currentItem={currentItem}
            title={t("Default lunch break")}
          />
        )}
      </>
    );
  }
  return <LoadingSpinner />;
};

export default DefaultSessionLengthsAndBreaks;
